import { useState } from 'react';
import { HelpCircle, Lightbulb } from 'lucide-react';

import FormModal from '../../../components/Modals/FormModal';
import {
  RESTAURANT_FORM_HELP,
  RestaurantHelpTopic,
} from './restaurantFormHelpContent';

interface Props {
  /** Clave del contenido de ayuda para este formulario. */
  topic: RestaurantHelpTopic;
  /** Ajusta la posición del botón. Por defecto flota arriba a la izquierda del modal. */
  variant?: 'modal' | 'inline';
}

/**
 * Botón con ícono de interrogación que abre un modal con la guía de
 * configuración del formulario de restaurante indicado en {@link Props.topic}.
 *
 * - `variant="modal"` (por defecto): se posiciona absoluto arriba a la izquierda,
 *   pensado para colocarlo dentro de un `FormModal` (junto al botón cerrar).
 * - `variant="inline"`: botón normal en el flujo, para encabezados de página.
 */
const RestaurantFormHelp = ({ topic, variant = 'modal' }: Props) => {
  const [open, setOpen] = useState(false);
  const content = RESTAURANT_FORM_HELP[topic];

  if (!content) return null;

  const trigger = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label="Ayuda para configurar este formulario"
      title="¿Cómo configurar esto?"
      className={
        variant === 'modal'
          ? 'absolute left-3 top-3 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full border border-stroke bg-white text-body transition hover:border-primary hover:text-primary dark:border-strokedark dark:bg-boxdark dark:text-bodydark dark:hover:text-primary'
          : 'inline-flex h-9 w-9 items-center justify-center rounded-lg border border-stroke bg-white text-body transition hover:border-primary hover:text-primary dark:border-strokedark dark:bg-boxdark dark:text-bodydark dark:hover:text-primary'
      }
    >
      <HelpCircle className="h-4 w-4" />
    </button>
  );

  return (
    <>
      {trigger}

      <FormModal
        isOpen={open}
        onClose={() => setOpen(false)}
        title={content.title}
        maxWidth="max-w-lg"
        zIndex="top"
      >
        <div className="space-y-5 text-sm">
          <p className="leading-relaxed text-body dark:text-bodydark">
            {content.intro}
          </p>

          <div className="space-y-3">
            {content.fields.map((field) => (
              <div
                key={field.label}
                className="rounded-lg border border-stroke bg-gray-2 p-3 dark:border-strokedark dark:bg-meta-4/40"
              >
                <p className="font-semibold text-black dark:text-white">
                  {field.label}
                </p>
                <p className="mt-0.5 leading-relaxed text-body dark:text-bodydark">
                  {field.text}
                </p>
              </div>
            ))}
          </div>

          {content.tips && content.tips.length > 0 && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
              <p className="flex items-center gap-1.5 font-semibold text-primary">
                <Lightbulb className="h-4 w-4" />
                Consejos
              </p>
              <ul className="mt-1.5 list-disc space-y-1 pl-5 text-body dark:text-bodydark">
                {content.tips.map((tip) => (
                  <li key={tip} className="leading-relaxed">
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </FormModal>
    </>
  );
};

export default RestaurantFormHelp;
